import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useRoutePageMetaSnapshot } from '../../../hooks/pageMetaStore';
import { useStaffMode } from '../../../hooks/StaffModeProvider';
import StaffButton from '../../staff/Button';
import { shouldSkipHelpCenterCategoryBreadcrumbSegment, getHelpCenterCategoryBreadcrumbActiveIndex, isHelpCenterCategoryPath, shouldSkipHelpCenterManageBreadcrumbSegment, getHelpCenterManageBreadcrumbActiveIndex } from '../../../pages/HelpCenter/helpCenterRoutes';

interface BreadcrumbItem {
  label: string;
  href: string;
  isActive?: boolean;
}

interface BreadcrumbProps {
  className?: string;
  loggedPerson?: {
    name?: string;
    lastName?: string;
  } | null;
  rightActions?: React.ReactNode;
}

function collapseConsecutiveSameLabel (items: BreadcrumbItem[]): BreadcrumbItem[] {
  if (items.length <= 1) {
    return items;
  }
  const out: BreadcrumbItem[] = [items[0]];
  for (let i = 1;i < items.length;i++) {
    const item = items[i];
    const prev = out[out.length - 1];
    if (prev.label === item.label) {
      out[out.length - 1] = { ...item };
    } else {
      out.push(item);
    }
  }
  return out;
}

export const isIdSegment = (segment: string): boolean => {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const numericIdPattern = /^\d+$/;
  return uuidPattern.test(segment) || numericIdPattern.test(segment);
};

/**
 * Splits all-lowercase slugs (e.g. `guestspeaker`) using words from catalog-style routes.
 * Longest match first.
 */
const MAX_COMPOUND_PART_LEN = 20;
const COMPOUND_SEGMENT_ACRONYMS = new Set(['wsg', 'aop']);
const COMPOUND_WORD_PARTS = new Set([
  'address', 'admin', 'admission', 'area', 'bar', 'catalogs', 'city', 'client', 'company',
  'contact', 'contacts', 'content', 'country', 'create', 'crisis', 'data', 'degree', 'edit',
  'education', 'firm', 'general', 'global', 'group', 'guest', 'industry', 'jurisdiction', 'language',
  'main', 'member', 'note', 'of', 'office', 'person', 'practice', 'preview', 'publication',
  'ranking', 'region', 'representative', 'school', 'speaker', 'state', 'sub', 'tag', 'topic', 'user',
  'view', 'aop', 'wsg',
]);

function tokenizeLowercaseCompound (s: string): string[] | null {
  const lower = s.toLowerCase();
  const tokens: string[] = [];
  let i = 0;
  while (i < lower.length) {
    let matched: string | null = null;
    const remaining = lower.length - i;
    for (let len = Math.min(MAX_COMPOUND_PART_LEN, remaining);len >= 1;len--) {
      const slice = lower.slice(i, i + len);
      if (COMPOUND_WORD_PARTS.has(slice)) {
        matched = slice;
        break;
      }
    }
    if (!matched) {
      return null;
    }
    tokens.push(matched);
    i += matched.length;
  }
  return tokens;
}

function formatTitleToken (token: string): string {
  const lower = token.toLowerCase();
  if (COMPOUND_SEGMENT_ACRONYMS.has(lower)) {
    return lower.toUpperCase();
  }
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** PascalCase, hyphens, or lowercase compounds (e.g. guestspeaker → Guest Speaker). */
export const titleFromPathSegment = (segment: string): string => {
  const withCaseBreaks = segment.replace(/([a-z\d])([A-Z])/g, '$1 $2');
  const parts = withCaseBreaks.split(/[-\s]+/).filter(Boolean);
  return parts
    .map(part => {
      if (/^[a-z]+$/.test(part)) {
        const compound = tokenizeLowercaseCompound(part);
        if (compound) {
          return compound.map(formatTitleToken).join(' ');
        }
      }
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
};

export const matchRoutePattern = (pathname: string, routes: Record<string, string>): string | null => {
  const pathSegments = pathname.toLowerCase().split('/').filter(Boolean);
  for (const [pattern, title] of Object.entries(routes)) {
    const patternSegments = pattern.toLowerCase().split('/').filter(Boolean);
    if (pathSegments.length !== patternSegments.length) {
      continue;
    }
    let isMatch = true;
    for (let i = 0;i < pathSegments.length;i++) {
      const pathSeg = pathSegments[i];
      const patternSeg = patternSegments[i];
      if (
        (patternSeg === 'id' ||
          patternSeg === ':id' ||
          patternSeg === 'generatedid' ||
          patternSeg === ':generatedid') &&
        (isIdSegment(pathSeg) ||
          // Bulk EmailLog ids are `emailsend-…`, not UUID.
          (pathSegments[0] === 'email-stats' && i === 1 && pathSeg.length > 0))
      ) {
        continue;
      }
      if (pathSeg !== patternSeg) {
        isMatch = false;
        break;
      }
    }
    if (isMatch) {
      return title;
    }
  }
  return null;
};

export const routeTitles: Record<string, string> = {
  '/mywsg': 'My WSG',
  '/settings': 'Settings',
  '/admin/settings': 'Settings',
  '/settings/two-factor-auth': 'Two-Factor Authentication',
  '/settings/change-password': 'Change Password',
  '/settings/single-sign-on': 'Single Sign-On',
  '/managefirm': 'Manage Firm',
  '/managefirm/professionals/id': 'Professionals Profiles',
  '/manage-firm/professionals': 'Professional Profiles',
  '/manage-firm/general': 'General',
  '/whats-new': 'What\'s New',
  '/aboutus': 'About Us',
  '/contact': 'Contact Us',
  '/site-updates': 'Site Updates',
  '/site-updates/contacts': 'Contacts',
  '/contacts.asp': 'Contact',
  '/publications': 'Firm Publications',
  '/publications/view': 'Publication Detail',
  '/presses': 'Firm Press Releases',
  '/presses/view': 'Press Release Detail',
  '/marketing-workshop-chamber-strategy': 'WSG Learning Institute',
  '/send-email': 'Send Email',
  '/send-email/scheduled-emails': 'Scheduled Emails',
  '/pending-scheduled-emails': 'Scheduled Emails',
  '/unsubscribed-list': 'Unsubscribed',
  '/email-design/add': 'Create New Email: Envelope Info',
  '/email-design/id/edit-envelope': 'Edit Envelope Info',
  '/email-design/id/edit': 'Edit email design',
  '/email-design/id/edit-html': 'Edit email design',
  '/email-design/id/logs': 'Logs',
  '/email-stats': 'Email Stats',
  '/email-stats/id': 'Email Details',
  '/benefits': 'Benefits of Membership',
  '/board': 'Governance',
  '/objectives': 'WSG Objectives',
  '/councils': 'Regional Councils',
  '/my-events': 'My Events',
  '/events-staff': 'Events',
  '/events/id/connections': 'Connections',
  '/aop': 'AOP Mapping',
  '/member-search': 'Member Search Result',
  '/advanced-member-search': 'Advanced Member Search',
  '/help-center': 'WSG Help Center',
  '/help-center/manage': 'Manage WSG Help Center',
  '/integrator-engine': 'Import Module Dashboard',
  '/integrator-engine/firms': 'Import Module Dashboard',
  '/integrator-engine/run-logs': 'Run logs',
  '/integrator-engine/professionals': 'Professionals',
};

function Breadcrumb ({ loggedPerson, rightActions }: BreadcrumbProps) {
  const location = useLocation();
  const dynamicPageMeta = useRoutePageMetaSnapshot(location.pathname);
  const breadcrumbAction = dynamicPageMeta?.breadcrumbAction;
  const { isStaffMode } = useStaffMode();

  const noBreadcrumbRoutes = [
    '/advanced-member-search',
    '/',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/support-test-reset-password',
    '/confirmlogin',
    '/two-factor-auth',
    '/auth/google/callback',
    '/auth/linkedin/callback',
    '/link-account-login',
    '/newUser',
    '/test-icon',
    '/login.asp?action=logoff',
    '/two-factor-acknowledgement',
  ].map(route => route.toLowerCase());

  const generateBreadcrumb = (): BreadcrumbItem[] => {
    const pathname = location.pathname.toLowerCase();
    if (noBreadcrumbRoutes.some(route => pathname === route || pathname.startsWith(route + '/'))) {
      return [];
    }

    const pathSegments = pathname.split('/').filter(Boolean);
    const pathSegmentsRaw = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems: BreadcrumbItem[] = [];
    const helpCenterActiveIndex = getHelpCenterCategoryBreadcrumbActiveIndex(pathSegments);
    const helpCenterManageActiveIndex =
      getHelpCenterManageBreadcrumbActiveIndex(pathSegments);
    const resolvedHelpCenterActiveIndex =
      helpCenterManageActiveIndex ?? helpCenterActiveIndex;

    breadcrumbItems.push({
      label: 'Home',
      href: '/',
      isActive: pathname === '/'
    });

    dynamicPageMeta?.breadcrumbAncestors?.forEach((ancestor) => {
      breadcrumbItems.push({
        label: ancestor.label,
        href: ancestor.href,
        isActive: false,
      });
    });

    let currentPath = '';
    let currentPathRaw = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const rawSeg = pathSegmentsRaw[index] ?? segment;
      currentPathRaw += `/${rawSeg}`;

      if (
        segment.toLowerCase() === 'professionals' &&
        pathSegments[index + 1] &&
        isIdSegment(pathSegments[index + 1]) &&
        pathSegments[index - 1]?.toLowerCase() === 'managefirm'
      ) {
        return;
      }

      if (shouldSkipHelpCenterCategoryBreadcrumbSegment(pathSegments, segment, index)) {
        return;
      }

      if (shouldSkipHelpCenterManageBreadcrumbSegment(pathSegments, index)) {
        return;
      }

      const dynamicLabel = dynamicPageMeta?.breadcrumbLabels?.[currentPath.toLowerCase()];

      if (
        isHelpCenterCategoryPath(pathname) &&
        index === 2 &&
        !dynamicLabel
      ) {
        return;
      }

      let title: string | null = routeTitles[currentPath.toLowerCase()] || null;

      if (dynamicLabel) {
        title = dynamicLabel;
      }
      if (!title) {
        title = matchRoutePattern(currentPath, routeTitles);
      }
      // Bulk EmailLog ids are `emailsend-…`, not UUID — still show "Email Details".
      if (
        !title &&
        pathSegments[0]?.toLowerCase() === "email-stats" &&
        index === 1
      ) {
        title = routeTitles["/email-stats/id"] || "Email Details";
      }
      if (!title) {
        if (isIdSegment(segment)) {
          return;
        }
        const rawSegment = pathSegmentsRaw[index] ?? segment;
        title = titleFromPathSegment(rawSegment);
      }

      breadcrumbItems.push({
        label: title,
        href: currentPathRaw,
        isActive:
          resolvedHelpCenterActiveIndex !== null
            ? index === resolvedHelpCenterActiveIndex
            : index === pathSegments.length - 1,
      });
    });

    return collapseConsecutiveSameLabel(breadcrumbItems);
  };

  const breadcrumbItems = generateBreadcrumb();

  if (breadcrumbItems.length === 0) {
    return null;
  }

  const hasRightActions = Boolean(breadcrumbAction?.label) || rightActions != null;
  return (
    <nav aria-label="breadcrumb">
      <ol className={`list-none text-white flex flex-col sm:flex-row flex-wrap font-normal text-sm md:text-base leading-6 m-0  
        ${hasRightActions ? 'sm:items-start' : 'sm:items-center'} ${!isStaffMode && 'border-t-2 border-white/50 py-5'}`}>
        <div className="flex flex-col items-center flex-wrap flex-1 min-h-0 md:flex-row md:justify-between gap-2 md:gap-4">
          <div className="flex items-center flex-wrap flex-1 min-h-0">
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="mb-0 pl-0 flex items-center">
                {item.isActive ? (
                  <span className="text-white font-bold">
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    className="text-white hover:text-gray-300 hover:underline transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                )}
                {index < breadcrumbItems.length - 1 && (
                  <span className="mx-2 text-white/70">{">"}</span>
                )}
              </li>
            ))}
          </div>

          {isStaffMode && breadcrumbAction?.label && (
            <div className="flex shrink-0 h-full items-center md:items-end self-start md:self-auto">
              <StaffButton
                text={breadcrumbAction.label}
                color="white"
                variant="ghost"
                size="fixed"
                onClick={() => {
                  breadcrumbAction.onClick?.();
                }}
                textClassName="text-[22px]"
                icon={breadcrumbAction.icon}
                labelNonVisible={['md']}
              />
            </div>
          )}
        </div>
        {location.pathname.toLowerCase() === '/mywsg' && loggedPerson?.name && loggedPerson?.lastName && (
          <li className="w-full sm:w-auto sm:ml-auto text-white text-lg md:text-xl mt-2 sm:mt-0">
            Welcome, <span className="font-semibold">{loggedPerson.name} {loggedPerson.lastName}</span>
          </li>
        )}
        {rightActions !== null && (
          <li className="w-full sm:w-auto sm:ml-auto mt-2 sm:mt-0 flex items-center">
            {rightActions}
          </li>
        )}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
